import dayjs, { Dayjs } from 'dayjs';
import { uniqBy } from 'lodash-es';
import { computed, makeObservable, observable, runInAction } from 'mobx';

import { OrderOption } from '~components/OrderBySelector/OrderBySelector';
import { AddOnCharge } from '~hooks/useAddOns/models';
import type { Job } from '~hooks/useJob/models';
import { PhaseBasic } from '~hooks/useProjects/models';
import { DriverDayQueryOrdersSort } from '~pages/Approvals/DriverDay/constants';
import {
  persistDateRange,
  retrievePersistedDateRange,
} from '~utils/dateRangePersistence';
import { RTU_WAITING_TIMEOUT_IN_MS } from '~utils/rtu/rtu-utils';
type DriverDayPageFilters = {
  customerAccountIds?: string[];
  driverIds?: string[];
  vendorAccountIds?: string[];
};

export type DriverDayPageFilterKey = keyof DriverDayPageFilters;

type DriverDayPageDateFilters = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

const APPLIED_DATE_FILTERS_STORAGE_KEY = 'driverDay_appliedDateFilters';

export default class DriverDayStore {
  addOnChargesByJob: Map<string, AddOnCharge[]> = new Map();
  phaseTypeheadsByDriverDayID: Record<string, PhaseBasic[]> = {};

  dateFilters: DriverDayPageDateFilters = {
    startDate: null,
    endDate: null,
  };
  driverDaySort: OrderOption<DriverDayQueryOrdersSort> = {
    orderBy: DriverDayQueryOrdersSort.DRIVER_DAY_DATE,
    sortBy: 'desc',
  };
  filters: DriverDayPageFilters = {};
  hasUnfilteredResults = false;
  isLoadingAddOnChargesByJob: boolean = false;
  shiftApprovalLoadingByDriverDayId: Record<string, boolean> = {};
  jobIdsByDriverDayId: Record<string, string[]> = {};
  jobByJobId: Record<string, Job> = {};
  jobsWaitingForRTU: Array<{ jobId: string; friendlyJobId: string }> = [];
  rtuDriverDayUpdates: Record<string, number> = {};
  rtuJobUpdates: Record<string, number> = {};

  constructor() {
    makeObservable(this, {
      addOnChargesByJob: observable,
      dateFilters: observable,
      driverDaySort: observable,
      filters: observable,
      hasUnfilteredResults: observable,
      isLoadingAddOnChargesByJob: observable,
      shiftApprovalLoadingByDriverDayId: observable,
      jobIdsByDriverDayId: observable,
      jobByJobId: observable,
      jobsWaitingForRTU: observable,
      rtuDriverDayUpdates: observable,
      rtuJobUpdates: observable,
      hasActiveNonSearchFilters: computed,
    });
  }

  // The quick-filter search never counts — any set date or entity filter means
  // the user narrowed the list (clear-filters hint trigger).
  get hasActiveNonSearchFilters() {
    const hasDateFilter = Boolean(this.dateFilters.startDate || this.dateFilters.endDate);
    const hasEntityFilter = Object.values(this.filters).some(
      (value) => value !== undefined && value.length > 0,
    );

    return hasDateFilter || hasEntityFilter;
  }

  setHasUnfilteredResults(hasUnfilteredResults: boolean) {
    runInAction(() => {
      this.hasUnfilteredResults = hasUnfilteredResults;
    });
  }

  setDateFilters(startDate: Dayjs | null, endDate: Dayjs | null) {
    runInAction(() => {
      this.dateFilters = {
        startDate,
        endDate,
      };
    });

    persistDateRange(APPLIED_DATE_FILTERS_STORAGE_KEY, {
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
    });
  }

  restoreDateFilters() {
    const storedDateRange = retrievePersistedDateRange(APPLIED_DATE_FILTERS_STORAGE_KEY);

    if (!storedDateRange) {
      return;
    }

    runInAction(() => {
      this.dateFilters = {
        // End of day, matching what a live picker selection carries (the Today
        // shortcut ends at 23:59:59.999): useDriverDays sends these bounds to
        // the API verbatim, so the restored range must keep its inclusive end.
        endDate: storedDateRange.endDate
          ? dayjs.tz(storedDateRange.endDate).endOf('day')
          : null,
        startDate: storedDateRange.startDate ? dayjs.tz(storedDateRange.startDate) : null,
      };
    });
  }

  setDriverDaySort(sort: OrderOption<DriverDayQueryOrdersSort>) {
    runInAction(() => {
      this.driverDaySort = sort;
    });
  }

  updateFilter(key: DriverDayPageFilterKey, value: string[] | undefined) {
    runInAction(() => {
      this.filters[key] = value;
    });
  }

  resetFilters() {
    runInAction(() => {
      this.filters = {};
    });
  }

  setJobIdsByDriverDayId(driverDayId: string, jobIds: string[]) {
    runInAction(() => {
      this.jobIdsByDriverDayId[driverDayId] = jobIds;
    });
  }

  setJobByJobId(jobId: string, job: Job) {
    runInAction(() => {
      this.jobByJobId[jobId] = job;
    });
  }

  setJobs(jobs: Job[]) {
    runInAction(() => {
      jobs.forEach((job) => {
        this.jobByJobId[job.id] = job;
      });
    });
  }

  setPhaseTypeheadsByDriverDayId(driverDayId: string, phases: PhaseBasic[]) {
    runInAction(() => {
      this.phaseTypeheadsByDriverDayID = {
        ...this.phaseTypeheadsByDriverDayID,
        [driverDayId]: phases,
      };
    });
  }

  setShiftApprovalLoading(driverDayId: string, loading: boolean) {
    runInAction(() => {
      this.shiftApprovalLoadingByDriverDayId[driverDayId] = loading;
    });
  }

  isShiftApprovalLoading(driverDayId: string): boolean {
    return this.shiftApprovalLoadingByDriverDayId[driverDayId] ?? false;
  }

  clearDriverDayData() {
    runInAction(() => {
      this.hasUnfilteredResults = false;
      this.jobIdsByDriverDayId = {};
      this.jobByJobId = {};
      this.addOnChargesByJob = new Map();
      this.phaseTypeheadsByDriverDayID = {};
      this.shiftApprovalLoadingByDriverDayId = {};
      this.jobsWaitingForRTU = [];
      this.driverDaySort = {
        orderBy: DriverDayQueryOrdersSort.DRIVER_DAY_DATE,
        sortBy: 'desc',
      };
    });
  }

  setJobsWaitingForRTU(jobIds: string[]) {
    runInAction(() => {
      const jobsPendingRTUResponse: DriverDayStore['jobsWaitingForRTU'] = [];

      for (const jobId of jobIds) {
        const job = this.jobByJobId[jobId];
        if (job) {
          jobsPendingRTUResponse.push({ jobId: job.id, friendlyJobId: job.jobId });
        }
      }

      setTimeout(() => {
        this.clearJobsWaitingForRTU(
          jobsPendingRTUResponse.map((job) => job.friendlyJobId),
        );
      }, RTU_WAITING_TIMEOUT_IN_MS);

      this.jobsWaitingForRTU = uniqBy(
        [...this.jobsWaitingForRTU, ...jobsPendingRTUResponse],
        (job) => job.jobId,
      );
    });
  }

  clearJobsWaitingForRTU(friendlyJobIds: string[]) {
    runInAction(() => {
      this.jobsWaitingForRTU = this.jobsWaitingForRTU.filter((entry) => {
        return !friendlyJobIds.includes(entry.friendlyJobId);
      });
    });
  }

  isJobWaitingForRTU(jobId: string): boolean {
    return this.jobsWaitingForRTU.some((job) => job.jobId === jobId);
  }

  triggerDriverDayRTURefetch(driverDayId: string) {
    runInAction(() => {
      this.rtuDriverDayUpdates[driverDayId] = Date.now();
    });
  }

  triggerJobRTURefetch(jobId: string) {
    runInAction(() => {
      this.rtuJobUpdates[jobId] = Date.now();
    });
  }
}
